import React, { useContext } from 'react';
import styled from 'styled-components/macro';
import useTranslations from '../../../hooks/useTranslations';
import { Container } from '../../../components/view/FormBuilderStatusesView';
import { SimpleFlexContainer } from '../../../components/layouts/ng-layouts/Containers';
import _BackButton from '../../../components/BackButton';
import { FormBuilderIntegrationsContext } from '../engine/FormBuilderIntegrationsEngine';
import { TextString } from '../../../components/Text';
import _Tabs from '../../../components/engine/TabsEngine';
import Tab from '@material-ui/core/Tab';
import _Animation from '../../../components/Animation';
import Button from '../../../components/Button';
import FileUploadEngine from '../../../components/engine/FileUploadEngine';
import InputBase from '../../../components/form/InputBase';
import Checkbox from '@material-ui/core/Checkbox';
import {
  Divider,
  FormControlLabel,
  TextField,
  Tooltip
} from '@material-ui/core';
import DisconnectDialog from '../../GeneralDialog';
import Switch from '@material-ui/core/Switch';
import SelectEngine from '../../../components/engine/SelectEngine';
import Colors from '../../Colors';

const BackButton = styled(_BackButton)`
  && {
    align-self: baseline;
  }
`;

const BackButtonFlexContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;

  button {
    margin-right: 1.5rem;
  }
`;

const Tabs = styled(_Tabs)`
  && {
    margin-bottom: 1.2em;
  }
`;

const AnimationContainer = styled.div`
  display: flex;
  overflow: hidden;
`;

const Animation = styled(_Animation)`
  && {
    width: 100%;
  }
`;

const SettingsPanel = styled.div`
  max-width: 800px;
`;

const SwitchRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;

const H2 = styled.h2`
  margin: 1.2rem 0 0;
  font-size: 18px;
  color: ${({ $disabled }) => ($disabled ? 'rgba(0,0,0,0.5)' : '#000')};
  &:after {
    content: '${({ required }) => (required ? '*' : '')}';
    color: ${Colors.OT_RED};
    font-size: 20px;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  margin-top: 0.8rem;
  justify-content: flex-end;
`;

const StyledTextField = styled(TextField)`
  && {
    margin-top: 1rem;
  }
`;

const DefaultsForNegativeContainer = styled.div`
  margin: 1rem 0 0;
  ${({ $disabled }) => ($disabled ? 'color: rgba(0,0,0,0.5);' : '')}
`;

const RiskAndReasonContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 1rem;
`;

const FormBuilderWorldcheckView = props => {
  const t = useTranslations();
  const { setCurrentIntegrationPage } = useContext(
    FormBuilderIntegrationsContext
  );
  const { AUTHENTICATION: AUTHENTICATION_TAB, SETTINGS: SETTINGS_TAB } =
    props.TAB_VALUES;

  return (
    <>
      <Container>
        <SimpleFlexContainer>
          <BackButtonFlexContainer>
            <BackButton onClick={() => setCurrentIntegrationPage('')} />
            <TextString
              content={`form-builder.integrations.${props.integrationType}.activate`}
            />
          </BackButtonFlexContainer>
          <Tabs
            value={props.activeTab}
            onChange={(event, newValue) => props.setActiveTab(newValue)}
          >
            <Tab
              label={t(
                `form-builder.integrations.${props.integrationType}.authentication`
              )}
            />
            <Tab
              label={t(
                `form-builder.integrations.${props.integrationType}.settings`
              )}
            />
          </Tabs>
          <AnimationContainer>
            <Animation
              in={props.activeTab === AUTHENTICATION_TAB}
              mountOnEnter // very important
              unmountOnExit // very important
              type="slide"
              direction="right"
              timeout={{
                enter: 250,
                exit: 100
              }}
            >
              <H2>
                {t(`form-builder.integrations.${props.integrationType}.secret`)}
              </H2>
              <InputBase
                variant="outlined"
                value={props.secret}
                onChange={e => props.setSecret(e.target.value)}
                onFocus={() => {}}
                error={false}
                customCSS="width: 50% !important;"
              />
              <H2>
                {t(`form-builder.integrations.${props.integrationType}.group`)}
              </H2>
              <InputBase
                variant="outlined"
                value={props.groupId}
                onChange={e => props.setGroupId(e.target.value)}
                onFocus={() => {}}
                error={false}
                customCSS="width: 50% !important;"
              />
              <H2>
                {t(
                  `form-builder.integrations.${props.integrationType}.account`
                )}
              </H2>
              <InputBase
                variant="outlined"
                value={props.accountId}
                onChange={e => props.setAccountId(e.target.value)}
                onFocus={() => {}}
                error={false}
                customCSS="width: 50% !important;"
              />
              <ButtonContainer>
                {props.worldcheckConnected ? (
                  <>
                    <Button
                      onClick={props.onSaveBtnClicked}
                      disabled={
                        !props.secret || !props.groupId || !props.accountId
                      }
                    >
                      {t(
                        `form-builder.integrations.${props.integrationType}.save`
                      )}
                    </Button>

                    <Button
                      onClick={() => props.setIsDisconnectDialogOpen(true)}
                      variant="outlined"
                      color="secondary"
                    >
                      {t(
                        `form-builder.integrations.${props.integrationType}.disconnect`
                      )}
                    </Button>
                  </>
                ) : (
                  <Button
                    onClick={props.onConnectBtnClicked}
                    disabled={
                      !props.secret || !props.groupId || !props.accountId
                    }
                  >
                    {t(
                      `form-builder.integrations.${props.integrationType}.connect`
                    )}
                  </Button>
                )}
              </ButtonContainer>
            </Animation>
            <Animation
              in={props.activeTab === SETTINGS_TAB}
              mountOnEnter // very important
              unmountOnExit // very important
              type="slide"
              direction="left"
              timeout={{
                enter: 250,
                exit: 100
              }}
            >
              <SettingsPanel>
                <SwitchRow>
                  <span>
                    {t(`form-builder.integrations.worldcheck.turn_on_ongoing`)}
                  </span>
                  <Switch
                    checked={props.watchlistType === 'ONGOING'}
                    onChange={props.onToggleSwitch}
                    color="primary"
                  />
                </SwitchRow>
                <Divider />
                <SwitchRow>
                  <span>
                    {t(`form-builder.integrations.worldcheck.auto_resolve`)}
                  </span>
                  <Switch
                    checked={props.isAutoResolve}
                    onChange={props.onToggleAutoResolve}
                    color="primary"
                  />
                </SwitchRow>
                <DefaultsForNegativeContainer $disabled={!props.isAutoResolve}>
                  {t('form-builder.integrations.worldcheck.select_default')}
                  <RiskAndReasonContainer>
                    <div>
                      <Tooltip title="Required">
                        <H2 required $disabled={!props.isAutoResolve}>
                          Risk
                        </H2>
                      </Tooltip>
                      <SelectEngine
                        disabled={!props.isAutoResolve}
                        searchable
                        virtualize
                        virtualiziztionProps={{ itemSize: 60, width: '100%' }}
                        data-testid={`world-check-match-risk`}
                        value={props.autoResolutionRisk}
                        onChange={props.setAutoResolutionRisk}
                        placeholder="Choose a risk"
                        options={props.riskOptions}
                      />
                    </div>
                    <div>
                      <Tooltip title="Required">
                        <H2 required $disabled={!props.isAutoResolve}>
                          Reason
                        </H2>
                      </Tooltip>
                      <SelectEngine
                        disabled={!props.isAutoResolve}
                        searchable
                        virtualize
                        virtualiziztionProps={{ itemSize: 60, width: '100%' }}
                        data-testid={`world-check-match-reason`}
                        value={props.autoResolutionReason}
                        onChange={props.setAutoResolutionReason}
                        placeholder="Choose a reason"
                        options={props.reasonOptions}
                      />
                    </div>
                  </RiskAndReasonContainer>
                  <Tooltip title="Required">
                    <H2 required $disabled={!props.isAutoResolve}>
                      Comment
                    </H2>
                  </Tooltip>
                  <StyledTextField
                    disabled={!props.isAutoResolve}
                    data-testid={'comment-field'}
                    multiline
                    fullWidth
                    rows={5}
                    variant="outlined"
                    value={props.autoResolutionComment}
                    onChange={e =>
                      props.setAutoResolutionComment(e.target.value)
                    }
                    onBlur={props.onBlurCommentField}
                  />
                </DefaultsForNegativeContainer>
              </SettingsPanel>
            </Animation>
          </AnimationContainer>
        </SimpleFlexContainer>
        <DisconnectDialog
          open={props.isDisconnectDialogOpen}
          title={`form-builder.integrations.${props.integrationType}.disconnect_title`}
          content={`form-builder.integrations.${props.integrationType}.disconnect_explanation`}
          variant="delete"
          buttons={[
            {
              text: 'dialog.cancel',
              variant: 'outlined'
            },
            {
              text: `form-builder.integrations.${props.integrationType}.confirm_disconnect`,
              color: 'secondary',
              onClick: 'confirm'
            }
          ]}
          handleConfirm={() => {
            props.onDisconnectBtnClicked();
          }}
          handleClose={() => props.setIsDisconnectDialogOpen(false)}
        />
      </Container>
    </>
  );
};

export default FormBuilderWorldcheckView;
